import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SyntheticDataBanner } from "@/components/SyntheticDataBanner";
import {
  ShieldCheck, AlertTriangle, CheckCircle2, ExternalLink, Loader2, ChevronDown,
  ScanSearch, Info, XCircle,
} from "lucide-react";

const STATE_STYLE = {
  flagged: { ring: "border-[#B01E28]/40", num: "text-[#B01E28]", chip: "bg-red-50 text-[#B01E28]", Icon: AlertTriangle },
  clear: { ring: "border-slate-200", num: "text-emerald-600", chip: "bg-emerald-50 text-emerald-700", Icon: CheckCircle2 },
  not_run: { ring: "border-slate-200", num: "text-slate-300", chip: "bg-slate-100 text-slate-500", Icon: ScanSearch },
  error: { ring: "border-amber-400", num: "text-amber-600", chip: "bg-amber-50 text-amber-800", Icon: XCircle },
};

function RowTable({ endpoint, testid }) {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState(null);
  const [more, setMore] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let live = true;
    api.get(endpoint)
      .then(({ data }) => { if (live) { setData(data); setRows(data.items || []); } })
      .catch((e) => live && setErr(formatApiErrorDetail(e)));
    return () => { live = false; };
  }, [endpoint]);

  // EVERY LIST PAGES THROUGH EVERYTHING, AND SAYS WHAT IT IS NOT SHOWING — his instruction,
  // 2026-10-14. The worst of these was the legacy-without-a-truck list: production holds 2,820 and
  // the screen quietly showed the newest 300. One place to fix it, because one component draws them
  // all.
  const loadMore = async () => {
    setMore(true);
    try {
      const next = (data.page || 1) + 1;
      const join = endpoint.includes("?") ? "&" : "?";
      const { data: d } = await api.get(`${endpoint}${join}page=${next}&page_size=${data.page_size || 50}`);
      setRows((prev) => [...(prev || []), ...(d.items || [])]);
      setData((prev) => ({ ...prev, ...d, items: undefined }));
    } catch (e) { setErr(formatApiErrorDetail(e)); } finally { setMore(false); }
  };

  if (err) return <div data-testid={`${testid}-error`} className="text-xs text-[#B01E28] px-3 py-2">{err}</div>;
  if (!data) return <div className="flex items-center gap-2 px-3 py-3 text-xs text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin" />Reading…</div>;
  const items = rows || [];
  if (!items.length) return <div data-testid={`${testid}-empty`} className="text-xs text-emerald-700 px-3 py-2">Nothing to report.</div>;

  return (
    <div data-testid={testid} className="overflow-x-auto">
      {data.what && <p className="text-xs text-slate-500 px-3 pt-2">{data.what}</p>}
      {data.cause && <p data-testid={`${testid}-cause`} className="text-xs text-slate-600 px-3 pt-2"><b>What causes it:</b> {data.cause}</p>}
      {data.note && <p className="text-xs text-slate-500 px-3 pt-2">{data.note}</p>}
      {data.total !== undefined && (
        <p data-testid={`${testid}-showing`} className="font-mono text-[11px] text-slate-500 px-3 pt-2">
          {items.length >= (data.total || 0) ? `showing all ${data.total}` : `showing ${items.length} of ${data.total}`}
        </p>
      )}
      <table className="w-full text-xs mt-2">
        <tbody>
          {items.map((r) => (r.line ? (
            /* PART-SHAPED ROWS (photo corrections, source reversals, legacy without a truck) —
               one link and one sentence, because these read as events rather than as trucks. */
            <tr key={r.id} data-testid={`${testid}-row-${r.code}`} className="border-t border-slate-100">
              <td className="px-3 py-1.5 font-mono whitespace-nowrap align-top">
                <Link to={r.link} className="text-slate-700 hover:text-[#B01E28] font-bold">{r.code || "open"}</Link>
              </td>
              <td className="px-3 py-1.5 text-slate-600">{r.line}</td>
            </tr>
          ) : (
            <tr key={r.id} data-testid={`${testid}-row-${r.stock_number}`} className="border-t border-slate-100">
              <td className="px-3 py-1.5 font-mono whitespace-nowrap align-top">
                <Link to={`/donors/${r.id}`} className="text-slate-700 hover:text-[#B01E28] font-bold">{r.stock_number}</Link>
                {r.synthetic || r.declared_synthetic
                  ? <span className="ml-1.5 font-bold text-[#B01E28]">SYNTHETIC</span>
                  : null}
              </td>
              <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap align-top">{r.vehicle || [r.year, r.make, r.model].filter(Boolean).join(" ") || "—"}</td>
              {"values" in r && (
                <td className="px-3 py-1.5 text-slate-600">
                  <b className="font-mono text-[#B01E28]">{r.count}</b> value{r.count === 1 ? "" : "s"} its
                  type <span className="font-mono">{r.category}</span> doesn't define:{" "}
                  {(r.values || []).map((v, i) => (
                    <span key={v.key} className="text-slate-700">
                      {i ? " · " : ""}{v.label} = <b className="font-mono">{String(v.value)}</b>
                    </span>
                  ))}
                </td>
              )}
              {"specs" in r && (
                <td className="px-3 py-1.5 whitespace-nowrap">
                  <b className="font-mono text-[#B01E28]">{r.specs}</b>
                  <span className="text-slate-400"> spec{r.specs === 1 ? "" : "s"} recorded</span>
                  {r.sheet_name && <span className="text-slate-400"> · sheet: {r.sheet_name}</span>}
                </td>
              )}
              {"signals" in r && (
                <td className="px-3 py-1.5 text-slate-500">
                  {(r.signals || []).join(" · ")}
                  {r.parts_attached > 0 && <b className="text-[#B01E28]"> · {r.parts_attached} part{r.parts_attached === 1 ? "" : "s"} attached</b>}
                </td>
              )}
            </tr>
          )))}
        </tbody>
      </table>
      {data.has_more && (
        <button type="button" data-testid={`${testid}-more`} disabled={more} onClick={loadMore}
          className="w-full text-xs font-semibold text-slate-600 hover:text-[#B01E28] border-t border-slate-100 py-2">
          {more ? "Reading…" : `Show more — ${(data.total || 0) - items.length} still to come`}
        </button>
      )}
      {"threshold_max_specs" in data && (
        <p className="text-[11px] text-slate-400 px-3 py-2">
          Threshold: a truck is listed when its build sheet is attached and it carries{" "}
          <b>{data.threshold_max_specs} specs or fewer</b> — a flat threshold on purpose, because
          this check is about a sheet that produced almost nothing. How complete a truck is gets
          measured against its own vehicle type's field set on the Vehicles list.
        </p>
      )}
    </div>
  );
}

function CheckCard({ c }) {
  const [open, setOpen] = useState(false);
  const s = STATE_STYLE[c.state] || STATE_STYLE.not_run;
  const { Icon } = s;
  return (
    <div data-testid={`check-${c.key}`} className={`rounded-2xl border bg-white shadow-sm ${s.ring}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Icon className={`w-4 h-4 ${s.num}`} strokeWidth={2.2} />
              <h3 className="text-sm font-bold text-[#1F1F1F]">{c.title}</h3>
              {c.informational && (
                <span className="rounded-full bg-slate-100 text-slate-500 text-[10px] px-2 py-0.5 font-bold uppercase tracking-wide">for review</span>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{c.what}</p>
            {c.detail && <p data-testid={`check-detail-${c.key}`} className="text-[11px] text-slate-400 mt-1">{c.detail}</p>}
          </div>
          <div className="text-right shrink-0">
            {c.count === null
              ? <span data-testid={`check-count-${c.key}`} className="font-display text-sm font-bold text-slate-400 uppercase tracking-wide">not run</span>
              : <span data-testid={`check-count-${c.key}`} className={`font-display text-3xl font-black ${s.num}`}>{c.count}</span>}
          </div>
        </div>

        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {c.cost === "heavy" && (
            <span className={`rounded-full text-[10px] px-2 py-0.5 font-bold uppercase tracking-wide ${s.chip}`}>full scan</span>
          )}
          {c.rows_endpoint ? (
            <button data-testid={`check-expand-${c.key}`} onClick={() => setOpen((v) => !v)}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#B01E28] hover:underline">
              {open ? "Hide the rows" : "Show the rows"}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          ) : (
            <Link data-testid={`check-open-${c.key}`} to={c.link}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#B01E28] hover:underline">
              {c.cost === "heavy" ? "Open the report to run it" : "Open the report"}
              <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          )}
          <span className="text-[11px] text-slate-400 ml-auto">lives on: {c.owner}</span>
        </div>
      </div>
      {open && c.rows_endpoint && (
        <div className="border-t border-slate-100 bg-slate-50/60 rounded-b-2xl">
          <RowTable endpoint={c.rows_endpoint} testid={`rows-${c.key}`} />
        </div>
      )}
    </div>
  );
}

export default function HealthChecksPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/health-checks");
      setData(data);
      setErr(null);
    } catch (e) {
      setErr(formatApiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const checks = data?.checks || [];
  const live = checks.filter((c) => c.state !== "not_run");
  const scans = checks.filter((c) => c.state === "not_run");

  return (
    <div data-testid="health-checks-page">
      <SyntheticDataBanner testid="health-synthetic-banner" />

      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-[#1F1F1F] flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-[#B01E28]" />Health &amp; Integrity
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Every integrity check in one place, with a link to the screen that owns the detail.
            {data?.note && <b className="text-slate-600"> {data.note}</b>}
          </p>
        </div>
        <Button data-testid="health-refresh" variant="outline" onClick={load} disabled={loading}
          className="rounded-lg border-slate-300 h-9">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
        </Button>
      </div>

      {err && (
        <div data-testid="health-error" className="rounded-xl border border-[#B01E28]/40 bg-red-50 px-4 py-3 text-sm text-[#B01E28] my-4">
          {err}
        </div>
      )}

      {data && (
        <div data-testid="health-summary" className="flex flex-wrap items-center gap-2 my-4 text-xs">
          <span className="rounded-full bg-red-50 text-[#B01E28] px-3 py-1 font-bold" data-testid="health-flagged">
            {data.counts.flagged} flagged
          </span>
          <span className="rounded-full bg-slate-100 text-slate-600 px-3 py-1" data-testid="health-visible">
            {data.counts.visible} checks visible to you
          </span>
          <span className="rounded-full bg-slate-100 text-slate-500 px-3 py-1" data-testid="health-notrun">
            {data.counts.not_run} full scans not run
          </span>
          {data.counts.errors > 0 && (
            <span className="rounded-full bg-amber-50 text-amber-800 px-3 py-1 font-bold" data-testid="health-errors">
              {data.counts.errors} could not run
            </span>
          )}
        </div>
      )}

      {!loading && data && checks.length === 0 && (
        <div data-testid="health-none-visible" className="rounded-xl border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500">
          No integrity checks are visible to your role.
        </div>
      )}

      {live.length > 0 && (
        <>
          <h2 className="text-base font-bold text-slate-700 mt-6 mb-2">Counted now</h2>
          <div className="grid gap-3 md:grid-cols-2">
            {live.map((c) => <CheckCard key={c.key} c={c} />)}
          </div>
        </>
      )}

      {scans.length > 0 && (
        <>
          <h2 className="text-base font-bold text-slate-700 mt-8 mb-2 flex items-center gap-2">
            Full scans — run on their own screen
            <span className="inline-flex items-center gap-1 text-[11px] font-normal text-slate-400">
              <Info className="w-3.5 h-3.5" />not run automatically, so opening this page stays fast
            </span>
          </h2>
          <div className="grid gap-3 md:grid-cols-2">
            {scans.map((c) => <CheckCard key={c.key} c={c} />)}
          </div>
        </>
      )}

      {data?.generated_at && (
        <p className="text-[11px] font-mono text-slate-400 mt-6">counted at {data.generated_at.slice(0, 16).replace("T", " ")}</p>
      )}
    </div>
  );
}
